/* ----------------------------------------------------------------------------
 * social-publish.ts — PURE request-building + response/error mapping + publish
 * gating logic for the Lane D gated PUBLISH action (D.2, the FE Publish
 * button). NO network here; the wiring lives in PublishControl.tsx via lib/api.
 * DOM-free + dependency-light so the truth-table runs under
 * `node --test --experimental-strip-types`.
 *
 * THE CONTRACT — mirrored EXACTLY from the wired, gated route + service
 * (line-cites verified against the files as merged; do NOT re-guess):
 *
 *   POST /v1/social/publish
 *     BASE_PATH '/v1/social/publish' (SocialPublishRoutes.php:41). lib/api's
 *     API_BASE already carries the '/v1' prefix, so the FE-relative path is
 *     '/social/publish' (matches sources-social.ts '/social/*' convention).
 *
 *   REQUEST body { draft_id:int>0, project_id?:int>0|null, options?:{ ... } }
 *     parsed by doPublish(): draft_id required + positive integer
 *     (Routes:110-114, posInt :186-196); project_id optional positive integer
 *     or null (Routes:118-120); options is a string-only whitelist —
 *     link_placement | suggested_first_comment | link (Routes:127).
 *     We send draft_id (+ options.link_placement only when a real placement is
 *     chosen) and DELIBERATELY OMIT project_id: the service routes STRICTLY by
 *     the draft's own domain and REFUSES a mismatched override PRE-NETWORK
 *     (override_domain_mismatch -> PublishRefusedException -> 422,
 *     SocialPublishService.php:190-196), so letting the draft self-route is the
 *     honest, drift-free path.
 *
 *   200 summary — route returns array_merge(['ok'=>true], $summary)
 *     (Routes:136) where $summary is the flat primary result + a 'results' list
 *     (SocialPublishService::publish :144-147). Each result (publishOne return)
 *     carries: ok, draft_id, platform, format, provider_ref, published_at,
 *     link_placement, first_comment_status. provider_ref / published_at /
 *     first_comment_status come from the adapter receipt (MetaGraphAdapter.php
 *     :221-225). NOTE: there is NO 'permalink' in the contract summary —
 *     provider_ref is the canonical post reference; a permalink is surfaced
 *     ONLY if the server ever adds one (optional, honest).
 *
 *   ERROR envelope { error:{ code, message } } (SocialPublishRoutes::error):
 *     409 publish_requires_approved  PublishNotApprovedException  (Routes:138)
 *     422 publish_refused            PublishRefusedException      (Routes:140)
 *     409 no_account_for_domain      NoAccountForDomainException  (Routes:142)
 *     503 provider_not_connected     NotConnectedException        (Routes:144)
 *     503 social_meta_disabled       flag off / no factory        (Routes:79,:89)
 *     404 draft_not_found            RuntimeException draft_not_found (Routes:146-147)
 *     500 publish_error              other RuntimeException       (Routes:149)
 *     400 invalid_body|invalid_draft_id|invalid_project_id (Routes:110,:114,:120)
 *     413 body_too_large                                          (Routes:85)
 *     403 no_active_workspace (:174) | forbidden (:179) — gate()
 *     401 workspace_context_required (:98) + session/CSRF WP_Error variants
 * -------------------------------------------------------------------------- */
import type { PublishState, ReviewStatus } from '@/types/social';
import type { MirrorLinkPlacement } from '@/data/social-mirror';

/** FE-relative publish path (API_BASE supplies '/v1'). Mirrors BASE_PATH (Routes:41). */
export const PUBLISH_ENDPOINT = '/social/publish';

/** Whitelisted, string-only publish options (Routes:127). */
export interface PublishOptions {
  link_placement?: string;
}

/** The request body shape (Routes:106-151 doPublish). */
export interface PublishRequest {
  draft_id: number;
  options?: PublishOptions;
}

/** One per-platform publish result (SocialPublishService::publishOne return :202-211). */
export interface PublishResult {
  ok: boolean;
  draft_id: number;
  platform: string;
  format: string;
  provider_ref: string;
  published_at: string;
  link_placement: string;
  first_comment_status: string;
  /** NOT in the contract summary; present only if the server ever adds it. */
  permalink?: string;
}

/** The normalised 200 outcome (primary result + the full results list). */
export interface PublishSuccess {
  ok: boolean;
  primary: PublishResult;
  results: PublishResult[];
}

/** A mapped, user-facing error (title + honest detail). */
export interface PublishErrorInfo {
  title: string;
  detail: string;
}

/* ==== (a) VISIBILITY + RE-PUBLISH GATE (B2) ================================ */

/**
 * TRUE when this draft ALREADY has a live provider receipt. The receipt path
 * (ContentRepository::recordProviderReceipt — the ONLY writer of
 * publish_state='published', always with a non-null provider_ref) is the source
 * of truth; either signal alone is treated as published (fail-closed against
 * duplicate live posts — the BE has no idempotency gate, so the FE must not
 * re-offer an unqualified "Publish").
 */
export function alreadyPublished(publishState: PublishState, providerRef: string | null): boolean {
  return publishState === 'published' || (typeof providerRef === 'string' && providerRef !== '');
}

/**
 * THE VISIBILITY RULE. The Publish action is offered ONLY when the draft is
 * approved (FE twin of the fail-closed service approval gate:
 * SocialPublishService.php:124-131 — review_status must be REVIEW_APPROVED,
 * else PublishNotApprovedException BEFORE any network call; route -> 409
 * Routes:138) AND it has NOT already been published (B2: an approved+published
 * draft would re-post live; explicit re-publish is NOT a D.2 capability).
 * Non-approved -> the control is ABSENT (never disabled-but-visible).
 * Approved+published -> the control renders the honest "Already published"
 * receipt state instead of a button.
 */
export function canPublish(
  reviewStatus: ReviewStatus,
  publishState: PublishState,
  providerRef: string | null,
): boolean {
  return reviewStatus === 'approved' && !alreadyPublished(publishState, providerRef);
}

/* ==== (b) SAVE-TRUTH GATE (B1) ============================================= */

/** The autosave signals the gate consumes (subset of useStudioAutosave). */
export interface PublishSaveSignals {
  status: 'idle' | 'saving' | 'saved' | 'error' | 'conflict';
  conflict: boolean;
  /** true when the editor state changed AFTER the confirm snapshot was taken. */
  dirtySinceConfirm: boolean;
}

export type PublishBlockReason = 'conflict' | 'save_failed' | 'saving' | 'draft_changed' | null;

/**
 * B1 — "confirm == what posts" gate. The POST body carries ONLY draft_id; the
 * BE publishes the PERSISTED row (SocialPublishService.php:119
 * ContentService::get). The confirm derives from LOCAL editor state, persisted
 * by a debounced autosave — so NO path may POST while local != persisted:
 *
 *   conflict      -> the server row is NEWER than what the user sees; a publish
 *                    would post content the user never reviewed. BLOCK.
 *   save_failed   -> local changes are NOT persisted and will NOT publish;
 *                    confirming would post the stale server row. BLOCK.
 *   saving        -> the flush (PublishControl calls saveNow() when the confirm
 *                    opens) is in flight; wait for the receipt. BLOCK.
 *   draft_changed -> the editor state changed after the confirm snapshot was
 *                    taken (the shown confirm is stale). BLOCK.
 *   null          -> settled: status saved (or idle == untouched since load,
 *                    when local == persisted by construction) and the snapshot
 *                    still matches. The confirm now shows the persisted truth.
 *
 * Precedence: conflict > save_failed > saving > draft_changed (most severe,
 * least transient first). Pure so the truth table is testable DOM-free.
 */
export function publishBlockReason(s: PublishSaveSignals): PublishBlockReason {
  if (s.conflict || s.status === 'conflict') return 'conflict';
  if (s.status === 'error') return 'save_failed';
  if (s.status === 'saving') return 'saving';
  if (s.dirtySinceConfirm) return 'draft_changed';
  return null;
}

/** Honest user-facing text per block reason (single source for render + tests). */
export function publishBlockMessage(reason: Exclude<PublishBlockReason, null>): string {
  switch (reason) {
    case 'conflict':
      return 'This draft was updated elsewhere. Reload the newer version before publishing — publishing now would post content you have not seen.';
    case 'save_failed':
      return 'Saving failed — your latest changes are NOT saved and would NOT publish. Fix the save first (see the editor banner), then try again.';
    case 'saving':
      return 'Saving your changes… publishing unlocks when the draft is saved.';
    case 'draft_changed':
      return 'The draft changed after this confirmation was opened. Cancel and reopen to review exactly what will post.';
  }
}

/* ==== (c) FIRST-COMMENT TRUTH SOURCES (B3) ================================= */

/** What the FE can prove about the draft's blog-link / first-comment inputs. */
export interface DraftLinkSources {
  /** The blog link the publisher would use, or null (SocialPostModel::linkSource). */
  link: string | null;
  /** The explicit first-comment cue, or null (SocialPostModel::firstCommentSource). */
  suggestedFirstComment: string | null;
  /**
   * TRUE when the FE CANNOT prove absence: the draft carries a repurpose
   * (blog_to_social) provenance blob, whose raw server-side JSON is the
   * documented carrier for link/suggested_first_comment
   * (SocialPostModel.php:316-318 "the value travels in provenance"), but the FE
   * adaptation (sources-social.ts adaptContent) strips the blob down to
   * capability/model/prompt_version/generated_at — so hidden link keys may
   * exist server-side that this client cannot see. The confirm must then show
   * an honest "may include a first-comment link" note for THIS class only,
   * never silence. Non-provenance drafts (aiProvenance null) have NO possible
   * carrier (no DB column, no writer), so no note.
   */
  maybeHiddenFirstComment: boolean;
}

function nonEmptyString(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}
function asRecord(v: unknown): Record<string, unknown> | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/**
 * Mirror the BE source-priority EXACTLY (no fabrication):
 *   link:  draft['link'] first, else provenance['link']
 *          (SocialPostModel::linkSource, SocialPostModel.php:302-312)
 *   first comment: draft['suggested_first_comment'] first, else
 *          provenance['suggested_first_comment']
 *          (SocialPostModel::firstCommentSource, SocialPostModel.php:319-330)
 * The FE item is read LOOSELY (both camelCase and raw snake_case keys are
 * probed, typed-narrowed) so if the adaptation ever starts passing these fields
 * through, the confirm shows the REAL value immediately with no code change.
 * Empty strings count as absent (the BE treats '' as no link content —
 * SocialPostModel::facebookPost hasLinkContent).
 */
export function extractDraftLinkSources(item: unknown): DraftLinkSources {
  const obj = asRecord(item);
  const prov = obj ? (asRecord(obj.aiProvenance) ?? asRecord(obj.ai_provenance)) : null;

  const link =
    nonEmptyString(obj?.link) ??
    nonEmptyString(prov?.link) ??
    null;
  const suggestedFirstComment =
    nonEmptyString(obj?.suggestedFirstComment) ??
    nonEmptyString(obj?.suggested_first_comment) ??
    nonEmptyString(prov?.suggested_first_comment) ??
    null;

  const capability = nonEmptyString(prov?.capability);
  const maybeHiddenFirstComment =
    link === null && suggestedFirstComment === null && capability === 'blog_to_social';

  return { link, suggestedFirstComment, maybeHiddenFirstComment };
}

/* ==== request building ===================================================== */

/** A positive integer or null (mirrors SocialPublishRoutes::posInt :186-196). */
function posInt(v: number): number | null {
  return Number.isInteger(v) && v > 0 ? v : null;
}

/**
 * Build the POST body. draft_id is required + positive (Routes:110-114). An
 * options.link_placement is included ONLY for a real, non-'none' placement
 * ('first_comment' | 'caption_bottom'); 'none'/undefined omit options entirely
 * so the body stays minimal and the server default applies. project_id is
 * deliberately omitted (self-route by domain; see file header).
 *
 * @throws RangeError when draftId is not a positive integer (fail-closed — the
 *   route would reject it 400, so we never send a knowingly-invalid body).
 */
export function buildPublishRequest(
  draftId: number,
  opts?: { linkPlacement?: MirrorLinkPlacement | null },
): PublishRequest {
  const id = posInt(draftId);
  if (id === null) {
    throw new RangeError('draft_id must be a positive integer');
  }
  const req: PublishRequest = { draft_id: id };
  const placement = opts?.linkPlacement;
  if (placement === 'first_comment' || placement === 'caption_bottom') {
    req.options = { link_placement: placement };
  }
  return req;
}

/* ==== response parsing ===================================================== */

/** Coerce one raw result object into a typed PublishResult (defensive). */
function toResult(raw: unknown): PublishResult {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const str = (k: string): string => (typeof r[k] === 'string' ? (r[k] as string) : '');
  const out: PublishResult = {
    ok: r.ok !== false,
    draft_id: typeof r.draft_id === 'number' ? r.draft_id : Number(r.draft_id) || 0,
    platform: str('platform'),
    format: str('format'),
    provider_ref: str('provider_ref'),
    published_at: str('published_at'),
    link_placement: str('link_placement'),
    first_comment_status: str('first_comment_status'),
  };
  if (typeof r.permalink === 'string' && r.permalink !== '') out.permalink = r.permalink;
  return out;
}

/**
 * Normalise the 200 body into { ok, primary, results }. The route ALWAYS merges
 * ok:true into a success (Routes:136), so ok is STRICT (`=== true`): a 200 body
 * missing `ok` is NOT treated as success (N5 — the caller must fail honest,
 * never render a "Published" receipt for an unrecognised body). 'results' is
 * the list from SocialPublishService::publish (:144-147); when absent
 * (defensive) the flat body IS the single result.
 */
export function parsePublishSuccess(res: unknown): PublishSuccess {
  const body = (res && typeof res === 'object' ? res : {}) as Record<string, unknown>;
  const rawResults = Array.isArray(body.results) ? body.results : null;
  const results = rawResults && rawResults.length > 0
    ? rawResults.map(toResult)
    : [toResult(body)];
  return { ok: body.ok === true, primary: results[0], results };
}

/* ==== error mapping ======================================================== */

/**
 * HONEST ERROR SURFACE. Map a route error to a user-facing message, keyed on
 * the contract CODE first (status alone is ambiguous — 409 is BOTH
 * publish_requires_approved AND no_account_for_domain; 503 is BOTH
 * provider_not_connected AND social_meta_disabled), with a status fallback for
 * unknown codes. 422 publish_refused surfaces the service's own refusal reason
 * verbatim-ish (it is the honest, non-leaking explanation from the choke-point).
 */
export function mapPublishError(err: { status: number; code?: string; message?: string }): PublishErrorInfo {
  const code = err.code ?? '';
  const msg = (err.message ?? '').trim();
  switch (code) {
    case 'publish_requires_approved': // 409 (Routes:138)
      return { title: 'Approval required', detail: 'This draft must be approved before it can be published.' };
    case 'no_account_for_domain': // 409 (Routes:142)
      return { title: 'No connected account', detail: 'No social account is connected for this draft’s domain, so it cannot be published. Connect an account for this domain and try again.' };
    case 'publish_refused': // 422 (Routes:140) — surface the service reason.
      return { title: 'Publish refused', detail: msg !== '' ? msg : 'This draft cannot be published in its current state.' };
    case 'provider_not_connected': // 503 (Routes:144)
      return { title: 'Publishing unavailable', detail: 'The connected account is not reachable right now. Reconnect it, then try again.' };
    case 'social_meta_disabled': // 503 (Routes:79,:89)
      return { title: 'Publishing unavailable', detail: 'Publishing is not enabled for this workspace yet.' };
    case 'draft_not_found': // 404 (Routes:146-147)
      return { title: 'Draft not found', detail: 'This draft could not be found in your workspace. Reload and try again.' };
    case 'invalid_body':
    case 'invalid_draft_id':
    case 'invalid_project_id': // 400 (Routes:110,:114,:120)
      return { title: 'Invalid request', detail: 'The publish request was invalid. Reload the draft and try again.' };
    case 'body_too_large': // 413 (Routes:85)
      return { title: 'Request too large', detail: 'The publish request was too large to send.' };
    case 'forbidden': // 403 (Routes:179 gate RBAC)
      return { title: 'Not permitted', detail: 'Only an owner or operator may publish.' };
    case 'no_active_workspace': // 403 (Routes:174)
    case 'workspace_context_required': // 401 (Routes:98 backstop)
      return { title: 'No active workspace', detail: 'Select a workspace, then try publishing again.' };
    case 'network_error': // lib/api ApiError(0, 'network_error')
      return { title: 'Network error', detail: 'Could not reach the server. Check your connection and try again.' };
    default:
      break;
  }
  // Status fallback for an unmapped code (fail honest, never fake success).
  if (err.status === 401) return { title: 'Session expired', detail: 'Your session has expired. Sign in again to publish.' };
  if (err.status === 403) return { title: 'Not permitted', detail: 'You do not have permission to publish this draft.' };
  if (err.status === 409) return { title: 'Cannot publish', detail: msg !== '' ? msg : 'This draft cannot be published in its current state.' };
  if (err.status === 422) return { title: 'Publish refused', detail: msg !== '' ? msg : 'This draft cannot be published as requested.' };
  if (err.status === 503) return { title: 'Publishing unavailable', detail: 'Publishing is temporarily unavailable. Try again later.' };
  if (err.status === 404) return { title: 'Not found', detail: 'The publish target could not be found.' };
  if (err.status === 400) return { title: 'Invalid request', detail: 'The publish request was invalid.' };
  return { title: 'Publish failed', detail: msg !== '' ? msg : 'Publishing failed. Please try again.' };
}
